import React from "react";
import { ArrowRight, CheckCircle2, ExternalLink, XCircle } from "lucide-react";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import type {
  FeaturesChecklistEntry,
  FeaturesGbpSummary,
  RouteMap,
} from "../types";

type FeaturesPageProps = {
  featuresChecklist?: FeaturesChecklistEntry[];
  routes: RouteMap;
  featuresGbpSummary: FeaturesGbpSummary;
};

const DIRECTORY_LINKS = [
  {
    name: "Google Business Profile",
    url: "https://business.google.com/create",
  },
  {
    name: "Yelp",
    url: "https://business.yelp.com/",
  },
];

const FeaturesPage: React.FC<FeaturesPageProps> = ({
  featuresChecklist,
  routes,
  featuresGbpSummary,
}) => {
  if (!featuresChecklist || featuresChecklist.length === 0) return null;

  const passedCount = featuresChecklist.filter((i) => i.passed).length;
  const score = Math.round((passedCount / featuresChecklist.length) * 100);

  const handleFeatureClick = (prompt: string, featureKey: string) => {
    const qs = new URLSearchParams();
    qs.set("prompt", prompt);
    qs.set("featureKey", featureKey);
    window.location.href = `${routes.dashboard}?${qs.toString()}`;
  };

  return (
    <DashboardPageLayout title="Site">
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="font-bold text-lg text-foreground">
                  Website & profile
                </div>
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-full border text-sm font-bold shrink-0 ${
                    score === 100
                      ? "bg-success/10 border-success/30 text-success"
                      : score >= 60
                        ? "bg-premium-50 border-premium-200 text-premium-700"
                        : "bg-destructive/10 border-destructive/30 text-destructive"
                  }`}
                >
                  {score}%
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {featuresChecklist.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => handleFeatureClick(item.prompt, item.key)}
                  className="w-full flex items-center justify-between px-4 py-3.5 hover:bg-muted/60 rounded-xl transition-colors group text-left cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    {item.passed ? (
                      <CheckCircle2 className="w-5 h-5 text-success shrink-0" />
                    ) : (
                      <XCircle className="w-5 h-5 text-destructive shrink-0" />
                    )}
                    <span
                      className={`font-medium text-sm ${item.passed ? "text-foreground" : "text-muted-foreground"}`}
                    >
                      {item.label}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {item.detail}
                    </span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-premium-500 transition-colors opacity-0 group-hover:opacity-100" />
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Directory listings</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-5">
                {DIRECTORY_LINKS.map((dir) => (
                  <li key={dir.name} className="group">
                    <a
                      href={dir.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 font-medium text-sm text-foreground group-hover:text-primary transition-colors"
                    >
                      {dir.name}
                      <ExternalLink className="w-3.5 h-3.5 opacity-50 group-hover:opacity-100" />
                    </a>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <GoogleBusinessProfileCard data={featuresGbpSummary} />
        </div>
      </div>
    </DashboardPageLayout>
  );
};

const GoogleBusinessProfileCard: React.FC<{
  data: FeaturesGbpSummary;
}> = ({ data }) => {
  const hasPlaceId = Boolean(data.googlePlaceId);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Google Business Profile</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border bg-muted/40 p-3 mb-4 text-xs space-y-1">
          <div>
            <span className="font-medium text-foreground">Name:</span>{" "}
            <span className="text-muted-foreground">{data.napName}</span>
          </div>
          {data.napPhone && (
            <div>
              <span className="font-medium text-foreground">Phone:</span>{" "}
              <span className="text-muted-foreground">{data.napPhone}</span>
            </div>
          )}
          {data.napServiceArea && (
            <div>
              <span className="font-medium text-foreground">Service area:</span>{" "}
              <span className="text-muted-foreground">
                {data.napServiceArea}
              </span>
            </div>
          )}
          <div>
            <span className="font-medium text-foreground">Website:</span>{" "}
            <span className="text-muted-foreground break-all">
              {data.siteUrl}
            </span>
          </div>
        </div>

        <ul className="text-xs text-muted-foreground space-y-2 mb-4 list-disc pl-4 leading-relaxed">
          <li>
            Use the exact name, phone, and service area shown above on GBP.
          </li>
          <li>Add your ToolKit site URL as the website on your GBP listing.</li>
          <li>
            Add 5+ photos of completed work and request reviews from happy
            customers.
          </li>
          <li>Reply to every review—Google rewards engagement.</li>
        </ul>

        {hasPlaceId && data.gbpReviewUrl && (
          <a
            href={data.gbpReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary"
          >
            Open review link
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </CardContent>
    </Card>
  );
};

export default FeaturesPage;
